import { Component, Inject, OnInit } from '@angular/core';

// RxJS
import { catchError, of, Subject, take, takeUntil } from 'rxjs';

// Lodash
import { cloneDeep } from 'lodash-es';

// Services
import { RecordsService } from '@modules/records/services/records.service';
import { UsersService } from '@modules/users/services/users.service';
import { NotificationService } from '@modules/elements/services/notification.service';
import { ProjectService } from '@modules/project/services/project.service';

// Types
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Record } from '@modules/records/types/record';
import { RRule, rrulestr, Frequency, Weekday, ByWeekday } from 'rrule';
import { RecordOptions } from '@modules/records/types/record-options';
import { UserPermissions } from '@modules/users/types/user';
import { Timezone } from '@modules/elements/types/timezone';
import { Project } from '@modules/project/types/project';
import { Router } from '@angular/router';


@Component({
    selector: 'app-create-record',
    templateUrl: './create-record.component.html',
    styleUrl: './create-record.component.less',
    standalone: false
})
export class CreateRecordComponent implements OnInit {

  // Public
  public isEdit = false;
  public record = new Record();
  public recordOptions = new RecordOptions();
  public rrule: RRule = new RRule({freq: Frequency.DAILY});
  public recordMetadata: {
    vod: {key: string, value: string}[],
    max: {key: string, value: string}[],
    linear: {key: string, value: string}[],
    archive: {key: string, value: string}[],
    c3: {key: string, value: string}[],
    hudsonYards: {key: string, value: string}[],
    additionalMetadata: {key: string, value: string}[],
    contentAcquisition: {key: string, value: string}[]
    s3Download: {key: string, value: string}[]
  } = { vod: [], max: [], linear: [], archive: [], c3: [], hudsonYards: [],  additionalMetadata: [], contentAcquisition: [], s3Download: [] };
  public minDate = new Date();
  public durationMinutes = 0;
  public dates = [{
    title: '',
    startTime: new Date(),
    timezone: 'America/New_York'
  }];
  public permissions?: UserPermissions[];
  public error?: string;
  public project?: Project;
  public metadataTasks = {
    vod: false,
    max: false,
    linear: false,
    archive: false,
    c3: false,
    contentAcquisition: false,
    s3Download: false
  };

  // Private
  private alive = new Subject<void>();
  private timezones = Timezone.options();

  /**
   * Constructor
   */

  constructor(
    public dialogRef: MatDialogRef<CreateRecordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {record?: Record},
    private recordsService: RecordsService,
    private usersService: UsersService,
    private projectService: ProjectService,
    private notificationService: NotificationService,
    private router: Router,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Set Default value
    this.setupMetadata();
    // Get Account
    if (this.data.record) {
      this.record = this.data.record;
      if (this.record.startTime) {
        this.dates[0].startTime = new Date(this.record.startTime);
      }
      if (this.dates[0].startTime && this.dates[0].startTime?.getTime() < this.minDate.getTime()) {
        this.dates[0].startTime.setFullYear(this.minDate.getFullYear());
        this.dates[0].startTime.setMonth(this.minDate.getMonth());
        this.dates[0].startTime.setDate(this.minDate.getDate());
      }
      this.parseMetadata();
      if (this.record.durationType === 'seconds') {
        this.durationMinutes = +(this.record.durationValue || 0) / 60;
      }
    }
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
    // Get Current Project
    this.projectService.getCurrentProject()
      .pipe(takeUntil(this.alive))
      .subscribe(project => {
        this.project = project;
        this.setupMetadataTasks();
        this.setupMetadata();
        if (this.data.record) {
          this.parseMetadata();
        }
      });
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  setupMetadataTasks(): void {
    this.metadataTasks.vod = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('vod'))) || false;
    this.metadataTasks.max = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('max'))) || false;
    this.metadataTasks.linear = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('linear'))) || false;
    this.metadataTasks.archive = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('archive'))) || false;
    this.metadataTasks.c3 = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('c3'))) || false;
    this.metadataTasks.contentAcquisition = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('contentAcquisition'))) || false;
    this.metadataTasks.s3Download = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type ==='s3downloadVideo' && task.body?.autoStartS3Download)) || false;
  }

  setupMetadata(): void {
    this.recordMetadata.vod = this.recordOptions.metadataVodDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.max = this.recordOptions.metadataMaxDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.linear = this.recordOptions.metadataLinearDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.archive = this.recordOptions.metadataArchiveDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.c3 = this.recordOptions.metadataC3DefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.hudsonYards = this.recordOptions.metadataHudsonYardsDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.contentAcquisition = this.recordMetadata.additionalMetadata = this.recordOptions.metadataContentAcquisitionDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.s3Download = this.recordOptions.metadataS3DownloadDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.additionalMetadata = this.recordOptions.metadataGeneralDefaultKeys.map(key => ({key, value: ''}));
    if (this.project?.serverTemplates && this.project?.serverTemplates?.length > 1) {
      this.recordMetadata.c3.push(...this.recordOptions.metadataC3BackupDefaultKeys.map(key => ({key, value: ''})));
    }
  }

  parseMetadata(): void {
    for (const key in this.record.additionalMetadata) {
      let index = this.recordMetadata.vod.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.vod[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.max.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.max[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.linear.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.linear[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.archive.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.archive[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.c3.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.c3[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.contentAcquisition.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.contentAcquisition[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.s3Download.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.s3Download[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.hudsonYards.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.hudsonYards[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.additionalMetadata.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.additionalMetadata[index].value = this.record.additionalMetadata[key];
        continue;
      }
      this.recordMetadata.additionalMetadata.push({key, value: this.record.additionalMetadata[key]});
    }
  }

  getMetadata(): {[key: string]: string} {
    const metadata: {[key: string]: string} = {}
    this.recordMetadata.vod.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.max.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.linear.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.c3.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.contentAcquisition.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.archive.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.hudsonYards.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.s3Download.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.additionalMetadata.forEach(item => metadata[item.key] = item.value);
    return metadata;
  }

  getDisplayKey(key: string): string {
    return this.recordOptions.overrideMetadataKeys[key] || key;
  }

  /**
   * Actions
   */

  updateTimezone(option: any, index: number): void {
    if (option && this.dates[index].timezone) {
      this.dates[index].timezone = option.value;
    }
  }
  updateDuration(option?: any): void {
    if (option && this.record.durationType) {
      this.record.durationType = option.value;
    }
  }

  addExtraDate(): void {
    const item = this.dates[this.dates.length - 1];
    const nextDate = new Date(item.startTime);
    nextDate.setDate(nextDate.getDate() + 7);
    this.dates.push({
      title: item.title,
      startTime: nextDate,
      timezone: item.timezone
    });
  }

  deleteExtraDate(index: number): void {
    this.dates.splice(index, 1);
  }

  addAdditionalMetadata(): void {
    let key = 'Key';
    let index = 1;
    while (this.recordMetadata.additionalMetadata.find(item => item.key === key)) {
      index++;
      key = `Key ${index}`;
    }
    this.recordMetadata.additionalMetadata.push({key: key, value: ''});
  }

  deleteAdditionalMetadata(index: number): void {
    this.recordMetadata.additionalMetadata.splice(index, 1);
  }

  updateDate(event: MatDatepickerInputEvent<Date>, index: number): void {
    if (event?.value) {
      const time = this.dates[index].startTime ? new Date(this.dates[index].startTime) : new Date();
      this.dates[index].startTime = event.value;
      this.dates[index].startTime.setSeconds(0);
      this.dates[index].startTime.setMinutes(time.getMinutes());
      this.dates[index].startTime.setHours(time.getHours());
      this.record.startTime = new Date(event.value).toISOString();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  create(): void {
    if (this.record) {
      for (let {title, startTime, timezone} of this.dates) {
        const record = cloneDeep(this.record);
        record.title = title;
        record.additionalMetadata = this.getMetadata();
        if (record.durationType === 'seconds') {
          record.durationValue = (this.durationMinutes * 60) + '';
        }
        startTime?.setSeconds(0);
        // console.log('[Record][timezone] ', startTime?.toLocaleString('en-US', {timeZone: timezone}));
        // console.log('[Record][getTimezoneOffset] ', startTime?.getTimezoneOffset());
        record.startTime = startTime?.toISOString();
        console.log('[Record][startTime] ', startTime);
        console.log('[Record] ', record);
        this.recordsService.createRecord(record)
          .pipe(
            catchError(error => {
              if (error) {
                this.error = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
              }
              return of();
            })
          )
          .subscribe((result) => {
            if (result) {
              const toast = this.notificationService.success(`Record "${record.title}" created successfully`, '', true);
              if (result.id) {
                toast.onTap
                  .pipe(take(1))
                  .subscribe(() => {
                    this.router.navigate(['/', result.projectId, 'records', result.id]);
                  });
              }
              this.dialogRef.close();
            }
          });
      }
    }
  }

}
